import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';

/**
 * Project routes that sit outside the phase layouts still have pages reading
 * projectId from the outlet context, so it has to be supplied here too —
 * otherwise useOutletContext() is null and destructuring it throws.
 */
const ProjectOutlet = () => {
  const { projectId } = useParams();
  return <Outlet context={{ projectId }} />;
};

// Guards
import { ProtectedRoute, GuestRoute } from './Guards';

// Layouts
import AppLayout from '../components/layout/AppLayout';
import ConstructionLayout from '../pages/construction/ConstructionLayout';
import CommunityLayout from '../pages/community/CommunityLayout';

// Loading fallback
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Lazy load pages for code splitting
const lazyLoad = (importFn) => {
  const Component = lazy(importFn);
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

// ─── Auth Pages ─────────────────────────────────────────────────────────────
const LoginPage = () => lazyLoad(() => import('../pages/auth/LoginPage'));
const SignupPage = () => lazyLoad(() => import('../pages/auth/SignupPage'));
const OnboardingPage = () => lazyLoad(() => import('../pages/auth/OnboardingPage'));
const ForgotPasswordPage = () => lazyLoad(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = () => lazyLoad(() => import('../pages/auth/ResetPasswordPage'));
const InviteAcceptPage = () => lazyLoad(() => import('../pages/auth/InviteAcceptPage'));

// ─── Core Pages ─────────────────────────────────────────────────────────────
const DashboardPage = () => lazyLoad(() => import('../pages/dashboard/DashboardPage'));
const PortfolioDashboardPage = () => lazyLoad(() => import('../pages/dashboard/PortfolioDashboardPage'));
const ProjectsPage = () => lazyLoad(() => import('../pages/projects/ProjectsPage'));
const ProjectDetailPage = () => lazyLoad(() => import('../pages/projects/ProjectDetailPage'));
const ProjectSettingsPage = () => lazyLoad(() => import('../pages/projects/ProjectSettingsPage'));
const UsersPage = () => lazyLoad(() => import('../pages/users/UsersPage'));
const MasterDataPage = () => lazyLoad(() => import('../pages/master-data/MasterDataPage'));
const NotificationsPage = () => lazyLoad(() => import('../pages/notifications/NotificationsPage'));
const AuditLogsPage = () => lazyLoad(() => import('../pages/audit-logs/AuditLogsPage'));
const NotFoundPage = () => lazyLoad(() => import('../pages/errors/NotFoundPage'));
const VendorsPage = () => lazyLoad(() => import('../pages/admin/VendorsPage'));
const AlertsPage = () => lazyLoad(() => import('../pages/admin/AlertsPage'));
const PlatformAdminPage = () => lazyLoad(() => import('../pages/admin/PlatformAdminPage'));

// ─── Construction Pages ─────────────────────────────────────────────────────
const AttendancePage = () => lazyLoad(() => import('../pages/construction/AttendancePage'));
const WorkProgressPage = () => lazyLoad(() => import('../pages/construction/WorkProgressPage'));
const MaterialInwardPage = () => lazyLoad(() => import('../pages/construction/MaterialInwardPage'));
const ExpensesPage = () => lazyLoad(() => import('../pages/construction/ExpensesPage'));
const SitePhotosPage = () => lazyLoad(() => import('../pages/construction/SitePhotosPage'));
const DayClosePage = () => lazyLoad(() => import('../pages/construction/DayClosePage'));
const MilestonePage = () => lazyLoad(() => import('../pages/construction/MilestonePage'));
const TasksPage = () => lazyLoad(() => import('../pages/construction/TasksPage'));
const BOQPage = () => lazyLoad(() => import('../pages/construction/BOQPage'));
const DrawingsPage = () => lazyLoad(() => import('../pages/construction/DrawingsPage'));
const SafetyPage = () => lazyLoad(() => import('../pages/construction/SafetyPage'));
const GeoPunchLogPage = () => lazyLoad(() => import('../pages/construction/GeoPunchLogPage'));
const ChangeOrdersPage = () => lazyLoad(() => import('../pages/construction/ChangeOrdersPage'));
const ReportsPage = () => lazyLoad(() => import('../pages/reports/ReportsPage'));
const DocumentsPage = () => lazyLoad(() => import('../pages/shared/DocumentsPage'));

// ─── Community Pages ────────────────────────────────────────────────────────
const CommunityDashboard = () => lazyLoad(() => import('../pages/community/CommunityDashboard'));
const UnitManagementPage = () => lazyLoad(() => import('../pages/community/UnitManagementPage'));
const ResidentsPage = () => lazyLoad(() => import('../pages/community/ResidentsPage'));
const BillingPage = () => lazyLoad(() => import('../pages/community/BillingPage'));
const ComplaintsPage = () => lazyLoad(() => import('../pages/community/ComplaintsPage'));
const WorkOrdersPage = () => lazyLoad(() => import('../pages/community/WorkOrdersPage'));
const NoticesPage = () => lazyLoad(() => import('../pages/community/NoticesPage'));
const ChatPage = () => lazyLoad(() => import('../pages/community/ChatPage'));
const VisitorPassesPage = () => lazyLoad(() => import('../pages/community/VisitorPassesPage'));
const StaffAttendancePage = () => lazyLoad(() => import('../pages/community/StaffAttendancePage'));
const AmenityBookingPage = () => lazyLoad(() => import('../pages/community/AmenityBookingPage'));
const PollsPage = () => lazyLoad(() => import('../pages/community/PollsPage'));
const ClassifiedsPage = () => lazyLoad(() => import('../pages/community/ClassifiedsPage'));

// ─── Handover Pages ─────────────────────────────────────────────────────────
const SnagsPage = () => lazyLoad(() => import('../pages/handover/SnagsPage'));
const HandoversPage = () => lazyLoad(() => import('../pages/handover/HandoversPage'));

// ─── Pages that only exist under the old project-ops tree ───────────────────
// These have no construction/ counterpart, so they are routed from where they
// live. They still carry the old Node/Mongo response shape and are due the same
// repair as the construction pages.
const TimelinePage = () => lazyLoad(() => import('../pages/project-ops/TimelinePage'));
const ReconciliationPage = () => lazyLoad(() => import('../pages/project-ops/ReconciliationPage'));
const AnomaliesPage = () => lazyLoad(() => import('../pages/project-ops/AnomaliesPage'));
const DigestSettingsPage = () => lazyLoad(() => import('../pages/project-ops/DigestSettingsPage'));
const GeofenceSettingsPage = () => lazyLoad(() => import('../pages/project-ops/GeofenceSettingsPage'));
const InvoicesPage = () => lazyLoad(() => import('../pages/invoicing/InvoicesPage'));
const InvoiceFormPage = () => lazyLoad(() => import('../pages/invoicing/InvoiceFormPage'));
const InvoiceDetailPage = () => lazyLoad(() => import('../pages/invoicing/InvoiceDetailPage'));

// ─── Public Pages ───────────────────────────────────────────────────────────
const HomePage = () => lazyLoad(() => import('../pages/home/HomePage'));

// ─── Router Definition ──────────────────────────────────────────────────────
const newRouter = createBrowserRouter([
  // Public routes
  { path: '/', element: <HomePage /> },
  { path: '/invite/:token', element: <InviteAcceptPage /> },

  // Guest-only routes (redirect to app if authenticated)
  {
    element: <GuestRoute />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },

  // Authenticated routes
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'portfolio', element: <PortfolioDashboardPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'onboarding', element: <OnboardingPage /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'master-data', element: <MasterDataPage /> },
      { path: 'vendors', element: <VendorsPage /> },
      { path: 'alerts', element: <AlertsPage /> },
      { path: 'audit-logs', element: <AuditLogsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'platform', element: <PlatformAdminPage /> },

      // ─── Project-scoped routes ──────────────────────────────────
      // URLs are flat under the project (…/projects/1/attendance), which is what
      // the sidebar links to. The phase layouts are pathless wrappers, so they
      // still frame their pages without adding a /construction or /community
      // segment. The sidebar decides which set to show, from the project's phase.
      {
        path: 'projects/:projectId',
        element: <ProjectOutlet />,
        children: [
          { index: true, element: <ProjectDetailPage /> },
          { path: 'settings', element: <ProjectSettingsPage /> },

          // Shared (any phase)
          { path: 'documents', element: <DocumentsPage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'chat', element: <ChatPage /> },

          // Construction phase
          {
            element: <ConstructionLayout />,
            children: [
              { path: 'attendance', element: <AttendancePage /> },
              { path: 'work-progress', element: <WorkProgressPage /> },
              { path: 'materials', element: <MaterialInwardPage /> },
              { path: 'expenses', element: <ExpensesPage /> },
              { path: 'site-photos', element: <SitePhotosPage /> },
              { path: 'day-close', element: <DayClosePage /> },
              { path: 'milestones', element: <MilestonePage /> },
              { path: 'tasks', element: <TasksPage /> },
              { path: 'boq', element: <BOQPage /> },
              { path: 'drawings', element: <DrawingsPage /> },
              { path: 'safety', element: <SafetyPage /> },
              { path: 'geo-punch', element: <GeoPunchLogPage /> },
              { path: 'change-orders', element: <ChangeOrdersPage /> },
            ],
          },

          // Handover phase. Snagging starts during construction, which is why the
          // backend allows both phases to reach it.
          { path: 'snags', element: <SnagsPage /> },
          { path: 'handovers', element: <HandoversPage /> },

          // Insights, settings and invoicing. Kept so switching routers does not
          // quietly drop features that only the old tree reached.
          { path: 'timeline', element: <TimelinePage /> },
          { path: 'reconciliation', element: <ReconciliationPage /> },
          { path: 'anomalies', element: <AnomaliesPage /> },
          { path: 'digest', element: <DigestSettingsPage /> },
          { path: 'geofence', element: <GeofenceSettingsPage /> },
          { path: 'invoices', element: <InvoicesPage /> },
          { path: 'invoices/new', element: <InvoiceFormPage /> },
          { path: 'invoices/:invoiceId', element: <InvoiceDetailPage /> },

          // Community phase
          {
            element: <CommunityLayout />,
            children: [
              { path: 'dashboard', element: <CommunityDashboard /> },
              { path: 'units', element: <UnitManagementPage /> },
              { path: 'residents', element: <ResidentsPage /> },
              { path: 'billing', element: <BillingPage /> },
              { path: 'complaints', element: <ComplaintsPage /> },
              { path: 'work-orders', element: <WorkOrdersPage /> },
              { path: 'notices', element: <NoticesPage /> },
              { path: 'visitor-passes', element: <VisitorPassesPage /> },
              { path: 'staff-attendance', element: <StaffAttendancePage /> },
              { path: 'amenities', element: <AmenityBookingPage /> },
              { path: 'polls', element: <PollsPage /> },
              { path: 'classifieds', element: <ClassifiedsPage /> },
            ],
          },
        ],
      },
    ],
  },

  // 404
  { path: '*', element: <NotFoundPage /> },
]);

export default newRouter;
